import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, User, Download, Trash2, Loader2, ChevronDown, FileText, Edit3, Check, X, Settings, ThumbsUp, ThumbsDown, Search, LogOut, Shield, Building, MessageSquarePlus, Copy, Menu, Mic, MicOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import MarkdownRenderer from './MarkdownRenderer';
import LoadingSequence from './LoadingSequence';
import ChatHistorySidebar from './ChatHistorySidebar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatInterface = ({ isAuthenticated = false }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingKey, setLoadingKey] = useState(0);
  const [currentSources, setCurrentSources] = useState([]);
  const [history, setHistory] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [filesDates, setFilesDates] = useState({});
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  // Correction state (negative vote)
  const [correctionIndex, setCorrectionIndex] = useState(null);
  const [correctionText, setCorrectionText] = useState('');
  // Mic state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // Recent files
  const [recentFiles, setRecentFiles] = useState([]);
  // Typewriter state
  const [typingIndex, setTypingIndex] = useState(-1);
  const [typingProgress, setTypingProgress] = useState(0);
  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);

  const lastAssistantRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auth: load username/role
  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedRole = localStorage.getItem('role') || 'user';
    if (storedUsername && storedUsername !== 'null' && storedUsername !== 'undefined') {
      setUsername(storedUsername);
    } else {
      setUsername('anonymous');
    }
    setUserRole(storedRole);
  }, [isAuthenticated]);

  // Load file dates + recent files (last 7 days)
  const loadFilesData = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/list_files`);
      const fileList = response.data.files || [];
      const datesMap = {};
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const recent = [];

      fileList.forEach(item => {
        if (Array.isArray(item) && item.length >= 3) {
          datesMap[item[0]] = item[2];
          const fileDate = new Date(item[2]);
          if (fileDate >= sevenDaysAgo) {
            const displayName = item[0].split('_').slice(1).join('_') || item[0];
            recent.push({ name: displayName, date: item[2] });
          }
        }
      });
      recent.sort((a, b) => new Date(b.date) - new Date(a.date));
      setFilesDates(datesMap);
      setRecentFiles(recent);
    } catch (error) {
      console.error('Erreur chargement fichiers:', error);
    }
  }, []);

  // Load suggestions
  const loadSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/suggestions`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    }
  }, []);

  // Load conversations list
  const loadConversations = useCallback(async () => {
    if (!username || username === 'anonymous') return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/conversations`, { params: { username } });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    }
  }, [username]);

  // Single init effect: fires once username is set and auth is confirmed
  useEffect(() => {
    if (!isAuthenticated || !username) return;
    loadFilesData();
    loadSuggestions();
    loadConversations();
  }, [isAuthenticated, username, loadFilesData, loadSuggestions, loadConversations]);

  // Typewriter effect
  useEffect(() => {
    if (typingIndex === -1) return;
    const msg = messages[typingIndex];
    if (!msg) { setTypingIndex(-1); return; }
    const words = msg.content.split(/(\s+)/);
    const total = words.length;

    const interval = setInterval(() => {
      setTypingProgress(prev => {
        const next = prev + 2;
        if (next >= total) {
          clearInterval(interval);
          setTypingIndex(-1);
          return total;
        }
        return next;
      });
    }, 25);
    return () => clearInterval(interval);
  }, [typingIndex, messages]);

  const getTypedContent = (content, progress) => {
    const words = content.split(/(\s+)/);
    return words.slice(0, progress).join('');
  };

  // Feature 1: Auto-scroll to start of last assistant message
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setTimeout(() => {
        lastAssistantRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [messages]);

  const findFileDate = (filename) => {
    if (filesDates[filename]) return filesDates[filename];
    const matchingKey = Object.keys(filesDates).find(key => key.includes(filename) || filename.includes(key));
    return matchingKey ? filesDates[matchingKey] : null;
  };

  const handleCopySection2 = (content) => {
    try {
      const sections = content.split(/\n\n/);
      let section2 = '';
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].trim();
        if (section.match(/^2\.\s*(Proposition|proposition|Argumentaire|argumentaire)/i)) {
          section2 = section;
          if (i + 1 < sections.length && !sections[i + 1].trim().match(/^\d+\./)) {
            section2 += '\n\n' + sections[i + 1];
          }
          break;
        }
      }
      if (!section2) {
        for (let i = 0; i < sections.length; i++) {
          if (sections[i].trim().startsWith('2.')) { section2 = sections[i].trim(); break; }
        }
      }
      const textToCopy = section2 ? section2.replace(/\[.*?\]/g, '').trim() : content.replace(/\[.*?\]/g, '').trim();
      navigator.clipboard.writeText(textToCopy);
      toast({ title: "Copié !", description: section2 ? "La proposition a été copiée" : "La réponse complète a été copiée", duration: 2000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de copier", variant: "destructive" });
    }
  };

  const handleLogout = () => { localStorage.clear(); window.location.reload(); };

  // New conversation
  const handleNewConversation = () => {
    setMessages([]);
    setHistory([]);
    setCurrentSources([]);
    setInputValue('');
    setEditingIndex(null);
    setCurrentConversationId(null);
    setCorrectionIndex(null);
    toast({ title: "Nouvelle discussion", description: "La conversation a été réinitialisée", duration: 2000 });
  };

  // Select conversation from sidebar
  const handleSelectConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/conversations/${conversationId}`);
      const conv = response.data;
      setMessages(conv.messages || []);
      const hist = (conv.messages || []).map(m => ({ role: m.role, content: m.content }));
      setHistory(hist);
      setCurrentConversationId(conversationId);
      setCurrentSources([]);
      setEditingIndex(null);
      setCorrectionIndex(null);
      setSidebarOpen(false);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger la conversation", variant: "destructive" });
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        handleNewConversation();
      }
      toast({ title: "Supprimée", description: "Discussion supprimée", duration: 2000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  // Save messages to conversation
  const saveToConversation = async (convId, newMessages) => {
    try {
      await axios.put(`${BACKEND_URL}/api/conversations/${convId}/messages`, { messages: newMessages });
    } catch (error) {
      console.error('Erreur sauvegarde conversation:', error);
    }
  };

  // Send message (accepts optional queryOverride for suggestions)
  const handleSendMessage = async (queryOverride) => {
    const query = queryOverride || inputValue.trim();
    if (!query || isLoading) return;
    if (!isAuthenticated) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Vous devez être authentifié pour utiliser Axia.' }]);
      return;
    }

    // Stop mic if active
    if (isListening) toggleMicrophone();

    const userMessage = { role: 'user', content: query, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setHistory(prev => [...prev, { role: 'user', content: query }]);

    const currentInput = query;
    setInputValue('');
    setIsLoading(true);
    setLoadingKey(prev => prev + 1);

    // Create conversation if this is the first message
    let convId = currentConversationId;
    if (!convId) {
      try {
        const title = currentInput.length > 50 ? currentInput.substring(0, 50) + '...' : currentInput;
        const res = await axios.post(`${BACKEND_URL}/api/conversations`, { username, title, messages: [userMessage] });
        convId = res.data.id;
        setCurrentConversationId(convId);
        loadConversations();
      } catch (e) {
        console.error('Erreur création conversation:', e);
      }
    } else {
      saveToConversation(convId, [userMessage]);
    }

    try {
      // Lancer la requête async — retourne un task_id immédiatement
      const { data: taskData } = await axios.post(`${BACKEND_URL}/api/aprolis-query`, {
        query: currentInput, prompt: '', n_context: 80, history: JSON.stringify(history), username: localStorage.getItem('username') || username || 'anonymous'
      }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 15000 });

      const taskId = taskData.task_id;
      if (!taskId) throw new Error('Pas de task_id');

      // Polling du résultat toutes les 2s (max 3 min)
      let result = null;
      const maxAttempts = 90;
      let notFoundCount = 0;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const { data: pollData } = await axios.get(`${BACKEND_URL}/api/task/${taskId}`, { timeout: 10000 });
          notFoundCount = 0;
          if (pollData.status === 'done' || pollData.status === 'error') {
            result = pollData;
            break;
          }
        } catch (pollErr) {
          if (pollErr.response && pollErr.response.status === 404) {
            notFoundCount++;
            if (notFoundCount >= 3) throw new Error('Tâche perdue (serveur redémarré)');
          }
        }
      }
      if (!result) throw new Error('Timeout polling');

      const assistantMessage = {
        role: 'assistant',
        content: result.response || 'Pas de réponse.',
        sources: result.context || result.sources || [],
        logId: result.log_id || null,
        rating: 1,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => {
        const newMessages = [...prev, assistantMessage];
        setTypingIndex(newMessages.length - 1);
        setTypingProgress(0);
        return newMessages;
      });
      setHistory(prev => [...prev, { role: 'assistant', content: result.response }]);
      if (result.context) setCurrentSources(result.context);

      // Save assistant message to conversation
      if (convId) saveToConversation(convId, [assistantMessage]);
    } catch (error) {
      console.error('Erreur API:', error);
      const errorMsg = { role: 'assistant', content: 'Désolé, je rencontre des difficultés. Veuillez réessayer.', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, errorMsg]);
    }

    setIsLoading(false);
    loadSuggestions();
  };

  // Send suggestion as message
  const handleSuggestionClick = (query) => {
    handleSendMessage(query);
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  // Feature 2: Negative vote requires corrected response
  const handleThumbsUp = async (messageIndex) => {
    const message = messages[messageIndex];
    if (!message.logId) return;
    try {
      await axios.post(`${BACKEND_URL}/api/feedback`, { log_id: message.logId, rating: 1 });
      const updated = [...messages];
      updated[messageIndex].rating = 1;
      setMessages(updated);
      setCorrectionIndex(null);
      toast({ title: "Feedback positif enregistré", description: "Merci !", duration: 2000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible d'enregistrer", variant: "destructive" });
    }
  };

  const handleThumbsDown = (messageIndex) => {
    const message = messages[messageIndex];
    if (!message.logId) return;
    setCorrectionIndex(messageIndex);
    setCorrectionText('');
    // Scroll vers le formulaire de correction après render
    setTimeout(() => {
      const panel = document.querySelector('[data-testid="correction-panel"]');
      if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const submitNegativeFeedback = async () => {
    if (correctionIndex === null) return;
    if (!correctionText.trim()) {
      toast({ title: "Commentaire requis", description: "Veuillez saisir un commentaire pour nous aider à améliorer Axia.", variant: "destructive", duration: 3000 });
      return;
    }
    const message = messages[correctionIndex];
    try {
      await axios.post(`${BACKEND_URL}/api/feedback`, { log_id: message.logId, rating: 0, corrected_response: correctionText });
      const updated = [...messages];
      updated[correctionIndex].rating = 0;
      setMessages(updated);
      setCorrectionIndex(null);
      setCorrectionText('');
      toast({ title: "Feedback négatif enregistré", description: "Merci pour votre correction !", duration: 2000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible d'enregistrer", variant: "destructive" });
    }
  };

  // Expand search
  const handleExpandSearch = async (messageIndex) => {
    let userQuery = '';
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') { userQuery = messages[i].content; break; }
    }
    if (!userQuery) { toast({ title: "Erreur", description: "Question originale introuvable", variant: "destructive" }); return; }
    setIsLoading(true);
    setLoadingKey(prev => prev + 1);
    try {
      const { data: taskData } = await axios.post(`${BACKEND_URL}/api/aprolis-proxy`, {
        query: userQuery, prompt: '', n_context: 40, history: '[]', spec_or_not: false, base_client_or_not: false, username: localStorage.getItem('username') || username || 'anonymous'
      }, { timeout: 15000 });

      const taskId = taskData.task_id;
      if (!taskId) throw new Error('Pas de task_id');

      let result = null;
      let notFoundCount = 0;
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const { data: pollData } = await axios.get(`${BACKEND_URL}/api/task/${taskId}`, { timeout: 10000 });
          notFoundCount = 0;
          if (pollData.status === 'done' || pollData.status === 'error') { result = pollData; break; }
        } catch (pollErr) {
          if (pollErr.response && pollErr.response.status === 404) {
            notFoundCount++;
            if (notFoundCount >= 3) throw new Error('Tâche perdue');
          }
        }
      }
      if (!result) throw new Error('Timeout');

      const expandedMessage = {
        role: 'assistant', content: result.response || '', sources: result.context || [],
        logId: result.log_id || null, rating: 1, isExpanded: true, timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, expandedMessage]);
      setCurrentSources(result.context || []);
      toast({ title: "Recherche élargie", description: "Nouvelle réponse avec champ étendu", duration: 3000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible d'élargir", variant: "destructive" });
    } finally { setIsLoading(false); }
  };

  const handleDownload = (filename) => { window.open(`${BACKEND_URL}/api/download?filename=${encodeURIComponent(filename)}`, '_blank'); };
  const handleRemoveSource = async (filename) => {
    try {
      await axios.get(`${BACKEND_URL}/api/removeSource?filename=${encodeURIComponent(filename)}`);
      setCurrentSources(prev => prev.filter(source => !source[0].includes(filename.split('.')[0])));
    } catch (error) { console.error('Erreur suppression:', error); }
  };

  const handleEditMessage = (index) => { setEditingIndex(index); setEditedContent(messages[index].content); };
  const handleSaveEdit = async (index) => {
    try {
      const originalMessage = messages[index];
      const userMessage = messages[index - 1];
      await axios.post(`${BACKEND_URL}/api/save-correction`, {
        user_query: userMessage?.content || '', original_response: originalMessage.content,
        corrected_response: editedContent, timestamp: new Date().toISOString()
      });
      const updated = [...messages];
      updated[index] = { ...updated[index], content: editedContent };
      setMessages(updated);
      setEditingIndex(null); setEditedContent('');
      toast({ title: "Réponse modifiée", description: "Correction sauvegardée.", duration: 3000 });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive", duration: 3000 });
    }
  };
  const handleCancelEdit = () => { setEditingIndex(null); setEditedContent(''); };

  // Feature 6: Microphone (MediaRecorder + Whisper backend)
  const toggleMicrophone = async () => {
    if (isListening) {
      // Stop recording
      recognitionRef.current?.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setIsListening(false);

        if (chunks.length === 0) return;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');

        toast({ title: "Transcription en cours...", duration: 2000 });

        try {
          const res = await axios.post(`${BACKEND_URL}/api/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
          });
          if (res.data.text) {
            setInputValue(prev => prev ? prev + ' ' + res.data.text : res.data.text);
            toast({ title: "Transcription terminée", duration: 2000 });
          }
        } catch (err) {
          console.error('Erreur transcription:', err);
          toast({ title: "Erreur", description: "Impossible de transcrire l'audio", variant: "destructive" });
        }
      };

      mediaRecorder.start();
      recognitionRef.current = mediaRecorder;
      setIsListening(true);
    } catch (err) {
      console.error('Erreur micro:', err);
      toast({ title: "Micro inaccessible", description: "Autorisez l'accès au microphone dans votre navigateur.", variant: "destructive" });
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      <Toaster />
      {/* Background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-200 to-blue-300" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-white/50 to-slate-100" />
      </div>
      <div className="absolute top-20 left-20 w-3 h-3 bg-cyan-500 rounded-full animate-pulse opacity-70 pointer-events-none" />
      <div className="absolute top-40 right-40 w-2 h-2 bg-blue-500 rounded-full animate-bounce opacity-50 pointer-events-none" />

      {/* Feature 4: Sidebar */}
      <ChatHistorySidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversations}
        activeConversationId={currentConversationId}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        onNewConversation={handleNewConversation}
        recentFiles={recentFiles}
      />

      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0 relative z-10">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Sidebar toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="h-9 w-9 p-0 text-slate-600 hover:bg-slate-100"
                data-testid="toggle-sidebar-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="relative">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg p-2">
                  <img src="https://customer-assets.emergentagent.com/job_mono-lift/artifacts/v6skdov3_Aprolis%20picto.png" alt="Aprolis Logo" className="w-full h-full object-contain" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Axia</h1>
                <p className="text-slate-600 text-xs sm:text-sm font-light">Aprolis - L'eXpertise amplifiée par l'IA (bêta)</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-slate-100 rounded-lg">
                <User className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{username}</span>
              </div>
              <Button onClick={handleNewConversation} variant="outline" className="hidden sm:flex items-center space-x-2 px-4 py-2 border-cyan-300 text-cyan-600 hover:bg-cyan-50" data-testid="new-conversation-header-btn">
                <MessageSquarePlus className="w-4 h-4" /><span className="hidden md:inline">Nouvelle discussion</span>
              </Button>
              <Button onClick={() => navigate('/documents')} variant="outline" className="hidden md:flex items-center space-x-2 px-4 py-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50">
                <Search className="w-4 h-4" /><span>Recherche documentaire</span>
              </Button>
              {userRole === 'admin' && (
                <Button onClick={() => window.open('/admin', '_blank')} variant="outline" className="hidden lg:flex items-center space-x-2 px-4 py-2 border-slate-300 hover:bg-slate-50">
                  <Settings className="w-4 h-4" /><span>Admin</span>
                </Button>
              )}
              <Button onClick={handleLogout} variant="outline" className="flex items-center space-x-2 px-3 sm:px-4 py-2 border-red-300 text-red-600 hover:bg-red-50" data-testid="logout-btn">
                <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto shadow-xl">
                  <Bot className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">Bonjour !</h2>
                  <p className="text-slate-600 max-w-md mx-auto leading-relaxed">
                    AXIA centralise les données techniques et marketing d'Aprolis, les comparatifs concurrent et plus de 4 000 cas clients pour renforcer l'efficacité de vos échanges commerciaux.
                  </p>
                </div>
              </div>
            </div>
          )}

          {messages.map((message, index) => {
            const isLastAssistant = message.role === 'assistant' && (index === messages.length - 1 || (index === messages.length - 2 && isLoading));
            return (
              <div key={index} ref={isLastAssistant ? lastAssistantRef : null}>
                <div className={`flex items-start space-x-3 sm:space-x-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.role === 'assistant' && (
                    <div className="relative flex-shrink-0">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg ${message.isExpanded ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gradient-to-br from-cyan-500 to-blue-600'}`}>
                        <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                    </div>
                  )}

                  <Card className={`transition-all duration-500 hover:shadow-xl border relative ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-lg border-slate-600 text-white max-w-4xl'
                      : editingIndex === index
                        ? 'bg-white/80 backdrop-blur-xl shadow-lg border-slate-200 max-w-6xl w-full'
                        : 'bg-white/80 backdrop-blur-xl shadow-lg border-slate-200 max-w-4xl'
                  }`}>
                    <CardContent className="p-4 sm:p-6">
                      {message.role === 'assistant' && message.isExpanded && (
                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                          <Search className="w-3 h-3 inline mr-1" />Recherche élargie
                        </div>
                      )}

                      {editingIndex === index && message.role === 'assistant' ? (
                        <div className="space-y-4">
                          <Textarea value={editedContent} onChange={(e) => setEditedContent(e.target.value)}
                            className="w-full min-h-[300px] bg-white/50 border-slate-300 text-slate-800 focus:border-cyan-500 resize-y text-base leading-relaxed" placeholder="Modifiez la réponse..." />
                          <div className="flex space-x-2 justify-end">
                            <Button size="sm" variant="outline" onClick={handleCancelEdit} className="bg-white border-slate-300 text-slate-600"><X className="w-4 h-4 mr-1" />Annuler</Button>
                            <Button size="sm" onClick={() => handleSaveEdit(index)} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg"><Check className="w-4 h-4 mr-1" />Sauvegarder</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative group">
                          {message.role === 'user' ? (
                            <p className="text-sm leading-relaxed whitespace-pre-wrap text-white">{message.content}</p>
                          ) : (
                            <MarkdownRenderer
                              content={index === typingIndex ? getTypedContent(message.content, typingProgress) : message.content}
                              className="text-slate-800"
                            />
                          )}
                          {/* Typing cursor */}
                          {index === typingIndex && (
                            <span className="inline-block w-0.5 h-5 bg-cyan-500 animate-pulse ml-0.5 align-text-bottom" />
                          )}
                          {/* Feedback + Edit Buttons for assistant (hidden during typewriter) */}
                          {message.role === 'assistant' && index !== typingIndex && (
                            <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center space-x-1">
                              <div className="flex items-center space-x-1 bg-white/90 backdrop-blur-sm rounded-lg border border-slate-200 shadow-sm p-1">
                                <Button size="sm" variant="ghost" onClick={() => handleThumbsUp(index)}
                                  className={`h-7 w-7 p-0 transition-colors ${message.rating === 1 ? 'bg-green-100 text-green-600' : 'hover:bg-green-50 hover:text-green-500 text-slate-400'}`}
                                  title="Pouce levé" data-testid={`thumbs-up-${index}`}>
                                  <ThumbsUp className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleThumbsDown(index)}
                                  className={`h-7 w-7 p-0 transition-colors ${message.rating === 0 ? 'bg-red-100 text-red-600' : 'hover:bg-red-50 hover:text-red-500 text-slate-400'}`}
                                  title="Pouce baissé" data-testid={`thumbs-down-${index}`}>
                                  <ThumbsDown className="w-3 h-3" />
                                </Button>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => handleEditMessage(index)}
                                className="h-8 w-8 p-0 bg-white/90 backdrop-blur-sm border border-slate-200 shadow-sm hover:bg-cyan-100 hover:text-cyan-600 text-slate-400" title="Modifier">
                                <Edit3 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Expand search + Copy buttons */}
                      {message.role === 'assistant' && !message.isExpanded && editingIndex !== index && index !== typingIndex && (
                        <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleExpandSearch(index)} disabled={isLoading}
                            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 bg-white hover:bg-blue-50 border-blue-300 text-blue-600">
                            <Search className="w-4 h-4" /><span>Élargir le champ de recherche</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleCopySection2(message.content)}
                            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 bg-white hover:bg-green-50 border-green-300 text-green-600">
                            <Copy className="w-4 h-4" /><span>Copier la réponse</span>
                          </Button>
                        </div>
                      )}

                      {/* Feature 2: Correction panel for negative vote */}
                      {correctionIndex === index && (
                        <div className="mt-4 pt-4 border-t border-red-200 space-y-3" data-testid="correction-panel">
                          <p className="text-sm font-medium text-red-600">Commentez cette réponse pour nous aider à améliorer Axia :</p>
                          <Textarea
                            value={correctionText}
                            onChange={(e) => setCorrectionText(e.target.value)}
                            className="w-full min-h-[120px] bg-red-50/30 border-red-200 text-slate-800 focus:border-red-400 resize-y text-sm"
                            placeholder="Écrivez ici la réponse correcte..."
                            data-testid="correction-textarea"
                          />
                          <div className="flex space-x-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => { setCorrectionIndex(null); setCorrectionText(''); }}
                              className="border-slate-300 text-slate-600" data-testid="cancel-correction-btn">
                              Annuler
                            </Button>
                            <Button size="sm" onClick={submitNegativeFeedback}
                              className="bg-red-500 hover:bg-red-600 text-white" data-testid="submit-correction-btn">
                              Valider le vote négatif
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {message.role === 'user' && (
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center shadow-lg">
                        <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Sources for this message */}
                {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                  <div className="flex items-start space-x-3 sm:space-x-4 justify-start mt-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0" />
                    <Card className="flex-1 max-w-4xl bg-white/80 backdrop-blur-xl border border-slate-200 shadow-lg">
                      <CardContent className="p-4">
                        <Collapsible className="w-full">
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between w-full cursor-pointer group hover:bg-slate-50 rounded-lg p-2 transition-all">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg">
                                  <FileText className="w-3 h-3 text-white" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-slate-800">Sources documentaires</h4>
                                  <p className="text-xs text-slate-500">Cliquez pour voir les {message.sources.length} sources</p>
                                </div>
                                <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-300">{message.sources.length}</Badge>
                              </div>
                              <ChevronDown className="w-4 h-4 text-slate-600 group-hover:text-cyan-600 transition-all group-data-[state=open]:rotate-180" />
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-3 space-y-2">
                            {message.sources.slice(0, 10).map((source, idx) => {
                              const filename = source[2];
                              const displayName = source[2].split('_').slice(1).join('_');
                              const isCompetitor = source[3] === '1';
                              const uploadDate = findFileDate(filename);
                              return (
                                <div key={idx} className={`flex items-center justify-between p-2 rounded-lg border transition-all hover:shadow-md ${
                                  isCompetitor ? 'bg-orange-50/50 border-orange-200' : 'bg-blue-50/50 border-blue-200'
                                }`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-5 h-5 rounded flex items-center justify-center shadow-sm ${isCompetitor ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-cyan-500'}`}>
                                        <FileText className="w-3 h-3 text-white" />
                                      </div>
                                      <span className="text-xs font-medium text-slate-800 truncate">{displayName}</span>
                                      <Badge variant="outline" className={`text-xs ${isCompetitor ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                                        {isCompetitor ? <div className="flex items-center space-x-1"><Building className="w-2 h-2" /><span>Concurrent</span></div>
                                          : <div className="flex items-center space-x-1"><Shield className="w-2 h-2" /><span>Aprolis</span></div>}
                                      </Badge>
                                      {uploadDate && (
                                        <span className="text-[10px] text-slate-400 italic">
                                          {new Date(uploadDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1 ml-4">
                                    <Button size="sm" variant="ghost" onClick={() => handleDownload(filename)}
                                      className="h-7 w-7 p-0 hover:bg-blue-100 hover:text-blue-600 text-slate-500" title="Télécharger">
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleRemoveSource(filename)}
                                      className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600 text-slate-500" title="Supprimer">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            );
          })}

          {/* Feature 3: Loading sequence */}
          {isLoading && (
            <div className="flex items-start space-x-3 sm:space-x-4 justify-start">
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <Card className="bg-white/80 backdrop-blur-xl border border-slate-200 shadow-lg">
                <CardContent className="p-4 sm:p-6">
                  <LoadingSequence key={loadingKey} />
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="relative bg-white/90 backdrop-blur-xl border-t border-slate-200 px-4 sm:px-8 py-4 sm:py-6 shadow-sm" style={{ overflow: 'visible' }}>
          {/* Suggestions flottantes au-dessus de l'input */}
          {suggestions.length > 0 && messages.length === 0 && !isLoading && (
            <div className="absolute bottom-full left-0 right-0 px-4 sm:px-8 pb-3 z-20">
              <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestions.map((query, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestionClick(query)}
                    className="text-left px-3 py-2.5 bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-xl text-sm text-slate-600 hover:bg-cyan-50/90 hover:border-cyan-300 hover:text-cyan-800 hover:shadow-md transition-all duration-200 truncate shadow-sm"
                    data-testid={`suggestion-${i}`}
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-2 sm:space-x-4 max-w-6xl mx-auto items-center">
            <div className="relative flex-1">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Quels sont les avantages des batteries lithium Aprolis ?"
                className="bg-white/80 backdrop-blur-xl border-slate-300 focus:border-cyan-500 focus:ring-cyan-200 text-slate-800 placeholder-slate-500 pr-4 py-6 text-base shadow-sm"
                disabled={isLoading}
                data-testid="chat-input"
              />
            </div>

            {/* Feature 6: Microphone button */}
            <Button
              onClick={toggleMicrophone}
              variant="outline"
              className={`flex-shrink-0 px-3 py-6 transition-all duration-300 ${
                isListening
                  ? 'bg-red-50 border-red-400 text-red-600 hover:bg-red-100 animate-pulse'
                  : 'border-slate-300 text-slate-500 hover:text-cyan-600 hover:border-cyan-400'
              }`}
              disabled={isLoading}
              title={isListening ? 'Arrêter la dictée' : 'Dictée vocale'}
              data-testid="mic-btn"
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>

            <Button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading || !isAuthenticated}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 sm:px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 flex-shrink-0"
              title={!isAuthenticated ? 'Authentification requise' : 'Envoyer'}
              data-testid="send-btn"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
